import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { EnumLanguage, ILanguageInfo, LANGUAGE_INFOS } from '@libs/constants';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { NgInputsModule, INgRadioButton } from '../../../inputs';
import { NgPipesModule } from '../../../../pipes';
import { INgYouTubeLanguageVideo, INgYouTubeVideo } from './youtube-video-preview.interface';


@Component({
    selector: 'ng-youtube-video-preview',
    templateUrl: './youtube-video-preview.component.html',
    styleUrls: ['./youtube-video-preview.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        NgInputsModule,
        NgPipesModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NgYoutubeVideoPreviewComponent implements OnInit {

	//** Input properties */
	@Input() youtubeVideoData  !: INgYouTubeVideo;
	@Input() isModalPreview 	: boolean = false;
	@Input() videoContainerWidth!: number;

	//** State Helpers */
	videoUrl 				!: string;
	videoLanguageOptions	 : Array<INgRadioButton[]> = [];
	selectedVideoLanguage 	!: EnumLanguage;


	/**------------------------------------------------------
	 * Angular live cycle hooks
	 */
	ngOnInit(): void {

		//0 - is videoData existing
		if (!this.youtubeVideoData) throw new Error(`NgYoutubeVideoPreviewComponent => ngOnInit => FATAL ERROR: youtubeVideoData cannot be left empty.`);

		//1 - get available video languages
		if (this.youtubeVideoData.videos?.length > 0) {
			this.videoLanguageOptions = this.getLanguagesInfoFromYoutubeVideo(this.youtubeVideoData.videos);
			this.onLanguageSelect();
		}
	}

	//** On Languages select */
	onLanguageSelect(): void {

		//0 - if no selected language existing then get the first position language from the array
		if (!this.selectedVideoLanguage) {
			this.selectedVideoLanguage = this.youtubeVideoData.videos[0].language;
			this.videoUrl = this.videoIdToEmbedUrl(this.youtubeVideoData.videos[0].videoId);
			return;
		}

		//1 - get the selected video data
		const video: INgYouTubeLanguageVideo = this.youtubeVideoData.videos.find((data: INgYouTubeLanguageVideo) => data.language === this.selectedVideoLanguage)!;
		if (!video) return;

		//2 - get the video data by the selected language
		this.selectedVideoLanguage = video.language;
		this.videoUrl = this.videoIdToEmbedUrl(video.videoId);
	}


	/**------------------------------------------------------
	 * Helper Functions
	 */
	private videoIdToEmbedUrl(videoId: string): string {
		return `https://www.youtube.com/embed/${videoId}?rel=0&enablejsapi=1`;
	}

	//** Gets languages from youtube video config */
	private getLanguagesInfoFromYoutubeVideo(youtubeVideo: INgYouTubeLanguageVideo[]): Array<INgRadioButton[]> {

		//0 - are youtube videos existing?
		if (!youtubeVideo || youtubeVideo?.length <= 0) return [];

		//1 - get data
		const availableLanguages: EnumLanguage[] = youtubeVideo.map((data: INgYouTubeLanguageVideo) => data.language);
		return this.getLanguagesInfoFromLanguages(availableLanguages);
	}

	//** Gets languages from array language enums */
	private getLanguagesInfoFromLanguages(languages: EnumLanguage[]): Array<INgRadioButton[]> {

		//0 - are languages existing?
		if (!languages || languages.length <= 0) return [];

		//1 - get language info by language code
		const languageOptions: INgRadioButton[] = [];
		for (const language of languages) {

			//a. is language info existing?
			const languageInfo: ILanguageInfo | undefined = LANGUAGE_INFOS[language];
			if (!languageInfo) continue;

			//b. generate data for radio button
			const languageInfoData: INgRadioButton = {
				code 	: languageInfo.code,
				name 	: languageInfo.code.toUpperCase(),
				tooltip : languageInfo.name
			};
			languageOptions.push(languageInfoData);
		}

		//2 - return the data
		return [languageOptions];
	}
}
